import React from 'react'
import { useParams, Link } from 'react-router-dom'
import PageHeader from '@/components/Common/PageHeader'
import SEOHead from '@/components/SEO/SEOHead'
import { clampDescription, shortenTitle } from '@/utils/meta'
import { buildGraph, breadcrumbListData, serviceSchema } from '@/data/structured-data'
import { medicalServices } from '@/data/services'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'

const ServiceDetail: React.FC = () => {
  const { slug } = useParams()
  const service = medicalServices.find(s => s.slug === slug)

  if (!service) {
    return (
      <section className="section-padding">
        <div className="container-custom">
          <div className="bg-white border rounded-xl p-8 text-center">
            <h2 className="text-xl font-bold mb-2">Hizmet bulunamadı</h2>
            <p className="text-gray-600 mb-4">Aradığınız hizmet mevcut değil.</p>
            <Link to="/services" className="text-primary-600 font-semibold">Hizmetlere dön</Link>
          </div>
        </div>
      </section>
    )
  }

  const telHref = toTelHref(contactInfo.phone)
  const waHref = toWhatsAppHref(contactInfo.whatsapp)

  return (
    <>
      <SEOHead
        title={shortenTitle(service.title)}
        description={clampDescription(service.description, 'Şanlıurfa Karaköprü Ardıç Veteriner Kliniği’nde bu hizmeti veriyoruz. Randevu için arayın.')}
        keywords={`şanlıurfa veteriner, ${service.title.toLowerCase()}, kedi kısırlaştırma, köpek kısırlaştırma, acil veteriner, röntgen fiyatları`}
        canonicalUrl={`/hizmetler/${service.slug}`}
        ogImage={service.image}
        structuredData={buildGraph(
          serviceSchema({
            name: service.title,
            description: service.description,
            url: `/hizmetler/${service.slug}`,
            image: service.image
          }),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Hizmetlerimiz', url: '/hizmetler' },
            { name: service.title, url: `/hizmetler/${service.slug}` }
          ])
        )}
      />
      <PageHeader title={service.title} subtitle={service.description} />
      <section className="section-padding">
        <div className="container-custom">
          <article className="bg-white rounded-xl border p-6 shadow">
            {service.image && (
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-64 object-cover rounded-lg mb-6"
                loading="lazy"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement
                  target.src = '/klinik.webp'
                }}
              />
            )}
            <h2 className="text-xl font-bold mb-3">{service.title}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{service.description}</p>

            {(service.highlights && service.highlights.length > 0) && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Öne Çıkanlar</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {service.highlights.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-2 bg-gray-50 border rounded-md p-3">
                      <span className="mt-1 text-primary-600">•</span>
                      <span className="text-gray-700">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(service.preparation && service.preparation.length > 0) && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Randevu Öncesi Hazırlık</h3>
                <div className="rounded-md border bg-amber-50 p-4">
                  <ul className="list-disc list-inside space-y-1 text-amber-900">
                    {service.preparation.map((p, idx) => (
                      <li key={idx}>{p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href={telHref}
                className="inline-flex items-center justify-center rounded-md bg-primary-600 text-white px-4 py-2 font-semibold hover:bg-primary-700 transition"
              >
                Telefonla Ara
              </a>
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-md bg-green-600 text-white px-4 py-2 font-semibold hover:bg-green-700 transition"
              >
                WhatsApp
              </a>
              <Link
                to="/iletisim"
                className="inline-flex items-center justify-center rounded-md border px-4 py-2 font-semibold hover:bg-gray-50 transition"
              >
                İletişim Sayfasına Git
              </Link>
            </div>
          </article>
        </div>
      </section>
    </>
  )
}

export default ServiceDetail


